/**
 * The cell editor's decision logic (Requirements 2.1–2.5, 2.9, 2.10, 3.1).
 *
 * Pure functions over explicit state, no DOM: `src/ui/cellEditor.ts` is the
 * textarea, the autocomplete listbox, and the caret, and it asks this module what
 * every keystroke *means*. The split is the same one the rest of the grid uses, and
 * it pays for itself twice here — the commit rules are arithmetic on addresses
 * (which cell the selection lands on, clamped at the sheet edge) and the editing
 * hints are pure functions of a string, so both are checkable in Node.
 *
 * Four decisions worth stating:
 *
 * **Classification happens in exactly one place, and it is not here.**
 * `core/format/classify.ts` owns Requirements 2.5–2.7. `commitPlan` calls it to
 * report what the typed text *is*, but the value that gets stored is whatever the
 * kernel's own `setCell` derives from the same function — the editor sends the raw
 * text in a command and never a pre-classified value. So there is one classifier,
 * one number-format decision, and no way for the editor's idea of `12.5%` to drift
 * from the Data_Store's.
 *
 * **Cancel dispatches nothing.** Escape closes the session and the cell is
 * untouched, because nothing was ever sent (Requirement 2.4). There is no restore
 * path to get wrong; {@link cancelEdit} reports the text the cell had so the caller
 * can put the formula bar back, and that is all.
 *
 * **Commit direction is data, not branching in the view.** Enter moves down, Tab
 * moves right, and the shifted forms move back the other way, each clamped at the
 * sheet edge (Requirements 2.2, 2.3). `Ctrl+Enter` fills the selected range instead
 * of moving at all (Requirement 3.1), which is the only commit that produces a
 * `setRegion` rather than a `setCell`.
 *
 * **Editing hints reuse the formula pipeline.** Reference highlighting is
 * `refsOf` over the parsed tree (Requirement 2.9) and autocomplete is
 * `functionNames(prefix)` over the registry (Requirement 2.10). Neither is
 * re-derived here: a half-typed formula that the parser rejects falls back to the
 * *tokenizer*, pairing `ref : ref` into ranges the way the grammar does, so
 * `=SUM(A1:B4` still highlights while the closing paren is missing.
 */

import { classifyInput, type ClassifiedInput } from '../core/format/classify.js';
import type { Command } from '../core/command/types.js';
import type { RefNode } from '../core/formula/ast.js';
import { tokenize } from '../core/formula/lexer.js';
import { parseFormula } from '../core/formula/parser.js';
import { cycleAnchorText, refUseToRange, refsOf, type RefUse } from '../core/formula/refs.js';
import { functionNames } from '../core/formula/registry.js';
import {
  MAX_COLS,
  MAX_ROWS,
  normalizeRange,
  type Addr,
  type Range,
  type SheetId,
} from '../core/types.js';

/**
 * Largest range `Ctrl+Enter` fills in one command.
 *
 * A whole-column selection is 5,000,000 cells, and a `setRegion` carrying five
 * million strings is a main-thread allocation nobody asked for. Past this cap the
 * plan degrades to the active cell alone and says so, rather than either freezing
 * the tab or silently dropping the fill.
 */
export const MAX_FILL_CELLS = 65_536;

/** Why an edit session started. */
export type EditIntent =
  /** A printable character replaced the cell's content (Requirement 2.1). */
  | 'type'
  /** `F2` opened the cell with its existing content (Requirement 3.1). */
  | 'edit';

/** One in-progress cell edit. */
export interface EditSession {
  addr: Addr;
  /** The cell's content before the edit began, for the formula bar on cancel. */
  prior: string;
  /** The text as it stands now. */
  text: string;
  intent: EditIntent;
}

/**
 * Opens a session on `addr`.
 *
 * `type` seeds with the character that started the edit and discards the prior
 * content; `edit` seeds with the prior content and leaves it to be modified.
 */
export function beginEdit(
  addr: Addr,
  prior: string,
  opts: { intent: EditIntent; seed?: string },
): EditSession {
  const text = opts.intent === 'type' ? (opts.seed ?? '') : prior;
  return { addr, prior, text, intent: opts.intent };
}

/** The session with new text. */
export function withText(session: EditSession, text: string): EditSession {
  return { ...session, text };
}

/** How a commit was confirmed. */
export type CommitKey =
  | 'enter'
  | 'shiftEnter'
  | 'tab'
  | 'shiftTab'
  /** `Ctrl+Enter`: fill the selection and stay put (Requirement 3.1). */
  | 'ctrlEnter';

/** Row/column step each commit key implies, before clamping. */
const COMMIT_STEP: Record<CommitKey, { dRow: number; dCol: number }> = {
  enter: { dRow: 1, dCol: 0 },
  shiftEnter: { dRow: -1, dCol: 0 },
  tab: { dRow: 0, dCol: 1 },
  shiftTab: { dRow: 0, dCol: -1 },
  ctrlEnter: { dRow: 0, dCol: 0 },
};

/**
 * Where the selection lands after a commit (Requirements 2.2, 2.3).
 *
 * Clamped to the sheet, so Enter on row 5,000,000 and Tab on column XFD stay where
 * they are rather than addressing a cell that does not exist.
 */
export function nextAddrAfterCommit(from: Addr, key: CommitKey): Addr {
  const step = COMMIT_STEP[key];
  return {
    sheet: from.sheet,
    row: Math.min(MAX_ROWS - 1, Math.max(0, from.row + step.dRow)),
    col: Math.min(MAX_COLS - 1, Math.max(0, from.col + step.dCol)),
  };
}

/** What a confirmed edit should do. */
export interface CommitPlan {
  /** The command that stores the content. */
  command: Command;
  /** Where the selection goes afterwards. */
  next: Addr;
  /** What the typed text is (Requirements 2.5–2.7), for notices and tests. */
  classified: ClassifiedInput;
  /** The range a `Ctrl+Enter` fill covers, or `null` for a single-cell commit. */
  filled: Range | null;
  /** A user-facing note, or `null`. Set when a fill was clamped. */
  notice: string | null;
}

/**
 * Plans a confirmed edit (Requirements 2.2, 2.3, 2.5, 3.1).
 *
 * `selection` matters only for `Ctrl+Enter`; every other key commits the one cell
 * the session is open on.
 */
export function commitPlan(
  session: EditSession,
  key: CommitKey,
  selection: Range,
): CommitPlan {
  const classified = classifyInput(session.text);
  const single: CommitPlan = {
    command: { k: 'setCell', addr: session.addr, input: session.text },
    next: nextAddrAfterCommit(session.addr, key),
    classified,
    filled: null,
    notice: null,
  };
  if (key !== 'ctrlEnter') return single;

  const range = normalizeRange(selection);
  const rows = range.r1 - range.r0 + 1;
  const cols = range.c1 - range.c0 + 1;
  if (rows * cols <= 1) return single;
  if (rows * cols > MAX_FILL_CELLS) {
    return {
      ...single,
      notice: `Ctrl+Enter fill was limited to the active cell because the selected range (${rows * cols}) exceeds ${MAX_FILL_CELLS} cells.`,
    };
  }

  const inputs: string[][] = [];
  for (let i = 0; i < rows; i++) inputs.push(new Array<string>(cols).fill(session.text));
  return {
    command: { k: 'setRegion', range, inputs },
    next: session.addr,
    classified,
    filled: range,
    notice: null,
  };
}

/** What a cancelled edit leaves behind (Requirement 2.4): the prior content. */
export function cancelEdit(session: EditSession): { command: null; restore: string } {
  return { command: null, restore: session.prior };
}

/**
 * Inserts a line break at the caret (`Alt+Enter`, Requirement 3.1).
 *
 * A selection is replaced, matching every other text input, and the caret lands
 * after the break.
 */
export function insertLineBreak(
  text: string,
  start: number,
  end: number,
): { text: string; caret: number } {
  const from = Math.max(0, Math.min(start, text.length));
  const to = Math.max(from, Math.min(end, text.length));
  return { text: `${text.slice(0, from)}\n${text.slice(to)}`, caret: from + 1 };
}

/* ------------------------------------------------------------------- hints */

/** One range the grid outlines while a formula is being edited (R2.9). */
export interface RefHighlight {
  /** Sheet the reference names, or `null` when it is unqualified. */
  sheetName: string | null;
  /** The rectangle, in the editing sheet's coordinates. */
  range: Range;
}

/** Stable identity of a highlight, so duplicates collapse. */
export function highlightKey(h: RefHighlight): string {
  const r = h.range;
  return `${h.sheetName ?? ''}!${r.r0}:${r.c0}:${r.r1}:${r.c1}`;
}

/** What the editor shows beside the caret while a formula is being edited. */
export interface EditorHints {
  /** True when the text is a formula (Requirement 2.5). */
  formula: boolean;
  /** Ranges referenced by the formula (Requirement 2.9), deduplicated. */
  highlights: RefHighlight[];
  /** The function-name prefix under the caret, or `null` when there is none. */
  prefix: string | null;
  /** Core_Formula_Set names matching {@link EditorHints.prefix} (R2.10). */
  suggestions: string[];
}

const NO_HINTS: EditorHints = { formula: false, highlights: [], prefix: null, suggestions: [] };

/** The sheet qualifier of a reference use, or `null` when unqualified. */
function sheetNameOf(use: RefUse): string | null {
  const ref: RefNode = use.k === 'ref' ? use : use.from;
  return ref.sheet ?? null;
}

function toHighlights(uses: readonly RefUse[], sheet: SheetId): RefHighlight[] {
  const seen = new Set<string>();
  const out: RefHighlight[] = [];
  for (const use of uses) {
    const highlight: RefHighlight = {
      sheetName: sheetNameOf(use),
      range: refUseToRange(use, sheet),
    };
    const key = highlightKey(highlight);
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(highlight);
  }
  return out;
}

/**
 * References of a formula the parser cannot accept yet.
 *
 * Half-typed formulas are the normal case while editing — `=SUM(A1:B4` has no
 * closing paren and `=A1+` has no right operand — and Requirement 2.9 highlights
 * *while* editing, so the tokenizer carries the fallback: every reference token,
 * with `ref : ref` runs folded into ranges the way the grammar's `:` operator does.
 */
function partialRefs(source: string): RefUse[] {
  const lexed = tokenize(source);
  if (!lexed.ok) return [];
  const uses: RefUse[] = [];
  const tokens = lexed.tokens;
  for (let i = 0; i < tokens.length; i++) {
    const token = tokens[i];
    if (token === undefined || token.kind !== 'ref') continue;
    const colon = tokens[i + 1];
    const tail = tokens[i + 2];
    if (colon?.kind === 'colon' && tail?.kind === 'ref') {
      uses.push({ k: 'range', from: token.ref, to: tail.ref });
      i += 2;
      continue;
    }
    uses.push(token.ref);
  }
  return uses;
}

/** Characters that may appear in a function name being typed. */
const NAME_CHAR = /[A-Za-z0-9_.]/;

/**
 * The function-name prefix ending at the caret (Requirement 2.10).
 *
 * `null` when the caret is not on a name at all — right after a digit, an operator,
 * or a closing paren — so the list appears only while a name is being typed. A
 * caret straight after `=` yields the empty prefix, which lists the whole
 * Core_Formula_Set.
 */
export function functionPrefixAt(text: string, caret: number): string | null {
  if (!text.startsWith('=')) return null;
  const at = Math.max(0, Math.min(caret, text.length));
  let start = at;
  while (start > 0 && NAME_CHAR.test(text.charAt(start - 1))) start -= 1;
  if (start === 0) return null; // the leading `=` is always at offset 0
  // An empty run means the caret is not on a name — right after an operator, a comma,
  // or a paren. The one exception is the caret straight after the leading `=`, where
  // the empty prefix legitimately lists the whole Core_Formula_Set.
  if (start === at) return at === 1 ? '' : null;
  const prefix = text.slice(start, at);
  // A run that starts with a digit is a number, not a name being typed.
  if (prefix.length > 0 && /^[0-9]/.test(prefix)) return null;
  return prefix;
}

/**
 * Everything the editor needs beside the caret (Requirements 2.9, 2.10).
 *
 * `caret` defaults to the end of the text, which is where it is while typing.
 */
export function editorHints(text: string, sheet: SheetId, caret?: number): EditorHints {
  if (!text.startsWith('=')) return NO_HINTS;
  const parsed = parseFormula(text);
  const uses = parsed.ok ? refsOf(parsed.ast) : partialRefs(text.slice(1));
  const prefix = functionPrefixAt(text, caret ?? text.length);
  return {
    formula: true,
    highlights: toHighlights(uses, sheet),
    prefix,
    suggestions: prefix === null ? [] : functionNames(prefix),
  };
}

/* ----------------------------------------------------------- anchor cycling */

/**
 * A reference or range token, for locating the one under the caret.
 *
 * The row run is unbounded on purpose. It used to be `[0-9]{1,7}`, which silently
 * stopped matching once the sheet grew past 9,999,999 rows and left `F4` doing nothing
 * on any reference below that line. Bounds are the lexer's job, not this pattern's.
 */
const REF_TOKEN_RE = /\$?[A-Za-z]{1,3}\$?[0-9]+(?::\$?[A-Za-z]{1,3}\$?[0-9]+)?/g;

function cycleOutOfBoundsRef(token: string): string | null {
  const match = /^(\$?)([A-Za-z]{1,3})(\$?)([0-9]+)$/.exec(token);
  if (!match) return null;
  const colAbsolute = match[1] === '$';
  const rowAbsolute = match[3] === '$';
  const state = `${colAbsolute ? 'c' : ''}${rowAbsolute ? 'r' : ''}`;
  const next = state === '' ? 'cr' : state === 'cr' ? 'r' : state === 'r' ? 'c' : '';
  return `${next.includes('c') ? '$' : ''}${match[2]}${next.includes('r') ? '$' : ''}${match[4]}`;
}

/**
 * Cycles the anchoring of the reference under the caret (`F4`, Requirement 3.1).
 *
 * The cycle itself is `cycleAnchorText` in `core/formula/refs.ts`; this is only the
 * search for the token to hand it. Returns `null` when the caret is not on a
 * reference, which is how the Shortcut_Handler decides to leave the text alone.
 */
export function cycleAnchorAtCaret(
  text: string,
  caret: number,
): { text: string; caret: number } | null {
  const at = Math.max(0, Math.min(caret, text.length));
  REF_TOKEN_RE.lastIndex = 0;
  for (let m = REF_TOKEN_RE.exec(text); m !== null; m = REF_TOKEN_RE.exec(text)) {
    const start = m.index;
    const end = start + m[0].length;
    // The caret counts as "on" a token when it sits inside it or immediately after
    // it, which is where the caret is the moment a reference has been typed.
    if (at < start || at > end) continue;
    const cycled = cycleAnchorText(m[0]) ?? cycleOutOfBoundsRef(m[0]);
    if (cycled === null) return null;
    return {
      text: `${text.slice(0, start)}${cycled}${text.slice(end)}`,
      caret: start + cycled.length,
    };
  }
  return null;
}

/* --------------------------------------------------------------- point mode */

/**
 * Characters after which a formula still wants an operand.
 *
 * A caret sitting after any of these is a place a reference can be written: `=`,
 * an operator, an opening paren, an argument separator, or a range colon. Trailing
 * whitespace is allowed, because `=SUM(A1, ` is the same position as `=SUM(A1,`.
 */
const OPERAND_EXPECTED = /[=+\-*/^&(,:<>%]\s*$/;

/**
 * A reference or range at the very end of the text, with an optional sheet qualifier.
 *
 * Used to decide whether a second click should *replace* the reference the first one
 * wrote rather than append beside it, which is what makes clicking around before
 * settling on a cell behave the way it does in Excel.
 */
const TRAILING_REF =
  /(?:(?:'[^']*'|[A-Za-z_][A-Za-z0-9_.]*)!)?\$?[A-Za-z]{1,3}\$?[0-9]+(?::\$?[A-Za-z]{1,3}\$?[0-9]+)?$/;

/** True when `index` sits mid-word, so a reference-shaped match is part of a longer name. */
function precededByWordChar(text: string, index: number): boolean {
  if (index === 0) return false;
  return /[A-Za-z0-9_.$!]/.test(text.charAt(index - 1));
}

/** A formula rewritten by pointing at a cell. */
export interface PointedRef {
  text: string;
  caret: number;
  /** Offset the reference was written at, so a drag can rewrite it in place. */
  start: number;
}

/**
 * Writes `ref` into a formula being edited, at the caret (Excel's "point mode").
 *
 * Clicking a cell while a formula is open contributes that cell to the formula; it
 * does not end the edit. This is the behaviour that was missing, and its absence was
 * destructive rather than merely inconvenient: a click committed the half-written
 * formula, so `=A1+` became a committed parse error and the user's work disappeared.
 *
 * Two positions accept a reference. After an operator or `=` the reference is
 * inserted; directly after an existing reference it *replaces* that reference, so
 * clicking B5 and then C6 leaves `=C6` rather than `=B5C6`. Anywhere else returns
 * `null`, and the caller leaves the formula untouched — declining to write is always
 * better than writing nonsense.
 *
 * Named ranges are why the replacement path checks the character before the match.
 * `es2024` at the tail of `=Sales2024` is reference-shaped on its own, and without the
 * check a click would rewrite the middle of the name. Note that a name like `LOG10` is
 * a different case and is *not* protected: `LOG` is a real column label, so `LOG10`
 * names a cell and replacing it is right.
 */
export function pointReference(text: string, caret: number, ref: string): PointedRef | null {
  if (!text.startsWith('=')) return null;
  const at = Math.max(0, Math.min(caret, text.length));
  const head = text.slice(0, at);
  const tail = text.slice(at);

  const trailing = TRAILING_REF.exec(head);
  if (trailing !== null && !precededByWordChar(head, trailing.index)) {
    const start = trailing.index;
    return { text: head.slice(0, start) + ref + tail, caret: start + ref.length, start };
  }
  if (OPERAND_EXPECTED.test(head)) {
    return { text: head + ref + tail, caret: at + ref.length, start: at };
  }
  return null;
}
